export interface WorkTimeRange {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

export type WorkTimeValidationError = 'required' | 'format' | 'order' | 'overlap';

const RANGE_PATTERN = /^(\d{1,2}):([0-5]\d)\s*-\s*(\d{1,2}):([0-5]\d)$/;

export function parseWorkTimeRanges(value: unknown): WorkTimeRange[] | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }

  const ranges = value.split(',').map(item => item.trim());
  if (ranges.some(item => item === '')) {
    return null;
  }

  const parsed = ranges.map(item => {
    const match = RANGE_PATTERN.exec(item);
    if (!match) return null;

    const range: WorkTimeRange = {
      startHour: Number(match[1]),
      startMinute: Number(match[2]),
      endHour: Number(match[3]),
      endMinute: Number(match[4])
    };
    return isValidTimeValue(range.startHour, range.startMinute, false) && isValidTimeValue(range.endHour, range.endMinute, true)
      ? range
      : null;
  });

  return parsed.every((range): range is WorkTimeRange => range !== null) ? parsed : null;
}

export function validateWorkTimeRanges(ranges: WorkTimeRange[]): WorkTimeValidationError | null {
  if (ranges.length === 0) return 'required';

  const normalized = ranges.map(range => ({
    ...range,
    start: toMinutes(range.startHour, range.startMinute),
    end: toMinutes(range.endHour, range.endMinute)
  }));

  if (
    normalized.some(
      range => !isValidTimeValue(range.startHour, range.startMinute, false) || !isValidTimeValue(range.endHour, range.endMinute, true)
    )
  ) {
    return 'format';
  }

  if (normalized.some(range => range.start >= range.end)) {
    return 'order';
  }

  normalized.sort((left, right) => left.start - right.start);
  for (let index = 1; index < normalized.length; index += 1) {
    if (normalized[index].start < normalized[index - 1].end) {
      return 'overlap';
    }
  }

  return null;
}

export function serializeWorkTimeRanges(ranges: WorkTimeRange[]): string {
  return [...ranges]
    .sort((left, right) => toMinutes(left.startHour, left.startMinute) - toMinutes(right.startHour, right.startMinute))
    .map(range => `${formatTime(range.startHour, range.startMinute)}-${formatTime(range.endHour, range.endMinute)}`)
    .join(',');
}

function isValidTimeValue(hour: number, minute: number, allowEndOfDay: boolean): boolean {
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute < 0 || minute > 59) {
    return false;
  }
  if (allowEndOfDay && hour === 24) return minute === 0;
  return hour >= 0 && hour <= 23;
}

function toMinutes(hour: number, minute: number): number {
  return hour * 60 + minute;
}

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
